import React, { useEffect, useState } from 'react';
import { BsDownload } from 'react-icons/bs';
import { API_URL } from '../../utils/api';
import { devApi } from './ajustesShared';

// Instalación del agente de ajustes en un VPS, con los comandos listos para pegar. Los
// ficheros del agente los sirve la propia app (con el token del servidor), así que no hace
// falta copiar nada del repositorio. La guía completa queda en /opt/ajustes/README.md.

const appBase = () => (API_URL || window.location.origin).replace(/\/$/, '');

const buildRootCommand = (info, deployUser) => `sudo bash -e -s <<'INSTALAR'
APP="${appBase()}"
TOK="${info.token}"
PUB="${info.publishToken}"
DEPLOY="${deployUser}"
id -u "$DEPLOY" >/dev/null 2>&1 || { echo "El usuario de despliegue $DEPLOY no existe"; exit 1; }
for c in git curl jq flock; do command -v "$c" >/dev/null || { echo "Falta $c (instálalo: apt install git curl jq util-linux)"; exit 1; }; done

# Usuario de Claude (sin sudo) y grupo compartido con el usuario de despliegue
id -u claudebot >/dev/null 2>&1 || adduser --disabled-password --gecos "Claude ajustes" claudebot
getent group ajustes >/dev/null || groupadd ajustes
usermod -aG ajustes claudebot; usermod -aG ajustes "$DEPLOY"

# Ficheros del agente (los descarga de Automationia con el token del servidor)
mkdir -p /opt/ajustes/{proyectos,deploy,runs,backups,runs-publicacion,backups-prod} /etc/claude-code
dl() { curl -fsS -H "X-Server-Token: $TOK" "$APP/api/dev-agent/files/$1" -o "$2"; }
for f in runner.sh publish.sh prompt.md schema.json deploy-ejemplo.sh CLAUDE.md.ejemplo README.md; do dl "$f" "/opt/ajustes/$f"; done
dl proyecto.env /opt/ajustes/proyectos/EJEMPLO.env
dl managed-settings.json /etc/claude-code/managed-settings.json && chmod 644 /etc/claude-code/managed-settings.json
printf 'APP_URL=%s\\nSERVER_TOKEN=%s\\n' "$APP" "$TOK" > /opt/ajustes/ajustes.env
printf 'PUBLISH_TOKEN=%s\\n' "$PUB" > /opt/ajustes/publicacion.env

# Permisos: claudebot solo ve lo suyo; el token de publicación solo lo lee el usuario de despliegue
chown root:ajustes /opt/ajustes /opt/ajustes/proyectos /opt/ajustes/ajustes.env
chmod 750 /opt/ajustes /opt/ajustes/proyectos; chmod 640 /opt/ajustes/ajustes.env
chmod 755 /opt/ajustes/runner.sh /opt/ajustes/publish.sh /opt/ajustes/deploy-ejemplo.sh
chown -R claudebot:claudebot /opt/ajustes/runs /opt/ajustes/backups; chmod 700 /opt/ajustes/runs /opt/ajustes/backups
chown -R "$DEPLOY" /opt/ajustes/runs-publicacion /opt/ajustes/backups-prod /opt/ajustes/publicacion.env /opt/ajustes/deploy
chmod 700 /opt/ajustes/runs-publicacion /opt/ajustes/backups-prod; chmod 600 /opt/ajustes/publicacion.env

# Cron: claudebot aplica, el usuario de despliegue publica; limpieza a los 14 días
touch /var/log/ajustes-runner.log /var/log/ajustes-publish.log
chown claudebot /var/log/ajustes-runner.log; chown "$DEPLOY" /var/log/ajustes-publish.log
cat > /etc/cron.d/ajustes <<CRON
PATH=/home/claudebot/.local/bin:/usr/local/bin:/usr/bin:/bin
*/5 * * * *  claudebot /opt/ajustes/runner.sh  >> /var/log/ajustes-runner.log 2>&1
*/5 * * * *  $DEPLOY /opt/ajustes/publish.sh >> /var/log/ajustes-publish.log 2>&1
30 5 * * *   root find /opt/ajustes/backups /opt/ajustes/backups-prod /opt/ajustes/runs /opt/ajustes/runs-publicacion -mindepth 1 -mtime +14 -delete
CRON
chmod 644 /etc/cron.d/ajustes
echo "OK: agente instalado en /opt/ajustes (guía completa en /opt/ajustes/README.md)"
INSTALAR`;

const CLAUDE_COMMAND = `sudo -iu claudebot
curl -fsSL https://claude.ai/install.sh | bash
claude auth login      # entra con tu cuenta de claude.ai (plan Max)
claude --version       # 2.1.259 o posterior
exit`;

const buildProjectCommand = (p) => `sudo cp /opt/ajustes/proyectos/EJEMPLO.env /opt/ajustes/proyectos/${p.id}.env
sudo chmod 640 /opt/ajustes/proyectos/${p.id}.env && sudo chgrp ajustes /opt/ajustes/proyectos/${p.id}.env
sudo nano /opt/ajustes/proyectos/${p.id}.env     # PROJECT_DIR, ENTORNO=${p.staging_url ? 'staging' : 'produccion'}, BACKUP_CMD${p.staging_url ? ', PUBLISH_CMD, PROD_DIR, PROD_DB' : ''}
${p.staging_url ? `sudo cp /opt/ajustes/deploy-ejemplo.sh /opt/ajustes/deploy/${p.id}.sh   # y ajústalo a su stack` : '# Sin staging: Claude trabaja sobre la app real tras el backup (sin paso de publicar)'}`;

const Block = ({ code, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_e) { /* selección manual */ }
  };
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="small text-muted">{label}</span>
        <button type="button" className={`btn btn-sm ${copied ? 'btn-success' : 'btn-outline-primary'}`} onClick={copy}>{copied ? '✓ Copiado' : 'Copiar'}</button>
      </div>
      <pre className="bg-dark text-light p-3 rounded mb-0" style={{ maxHeight: 260, overflow: 'auto', fontSize: '0.74rem', whiteSpace: 'pre' }}>{code}</pre>
    </div>
  );
};

const AgentInstallModal = ({ server, onClose }) => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [deployUser, setDeployUser] = useState('www-data');

  useEffect(() => {
    devApi(`/servers/${server.id}/install-info`).then(setInfo).catch((err) => setError(err.message));
  }, [server.id]);

  const user = deployUser.trim().replace(/[^a-z0-9_.-]/gi, '') || 'www-data';
  const rotateToken = async (which) => {
    const label = which === 'publish' ? 'de publicación' : 'del agente';
    if (!window.confirm(`El token ${label} anterior dejará de funcionar al instante: tendrás que volver a pegar el bloque 1 en el servidor. ¿Generar otro?`)) return;
    try {
      const d = await devApi(`/servers/${server.id}/rotate-token`, { method: 'POST', body: { which } });
      setInfo((prev) => ({ ...prev, [which === 'publish' ? 'publishToken' : 'token']: d.token }));
    } catch (err) { setError(err.message); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', overflowY: 'auto' }}>
      <div className="card" style={{ width: '100%', maxWidth: 900 }}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong><BsDownload className="me-2" />Instalar el agente de ajustes en «{server.name}»</strong>
          <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {!info && !error && <div className="text-muted">Cargando…</div>}
          {info && (
            <>
              <p className="small mb-2">
                Conéctate al servidor por SSH y pega los bloques en orden. El agente recoge los ajustes aprobados de este servidor, los aplica con Claude Code y, cuando publicas, otro usuario los pasa a producción.
              </p>
              <div className="alert alert-warning py-2 small d-flex justify-content-between align-items-center gap-2 flex-wrap">
                <span>Los comandos llevan los <strong>dos tokens</strong> de este servidor de ajustes (el del agente, que usa claudebot, y el de publicación): no los compartas. Requisitos: Ubuntu 20.04+/Debian 10+ con <code>git</code>, <code>curl</code>, <code>jq</code> y <code>flock</code>.</span>
                <span className="d-flex gap-1">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => rotateToken('agent')}>Rotar token del agente</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => rotateToken('publish')}>Rotar token de publicación</button>
                </span>
              </div>

              <div className="row g-2 align-items-end mb-3">
                <div className="col-sm-6">
                  <label className="form-label small mb-1">Usuario de despliegue (el dueño de las webs en producción)</label>
                  <input className="form-control form-control-sm" value={deployUser} onChange={(e) => setDeployUser(e.target.value)} placeholder="www-data, deploy, el usuario de Plesk…" />
                </div>
                <div className="col-sm-6 small text-muted">Es quien ejecuta la publicación. Claude trabaja con otro usuario, <code>claudebot</code>, que no puede tocar producción.</div>
              </div>

              <h6>1. Instalar el agente <span className="text-muted fw-normal small">(como root o con sudo)</span></h6>
              <Block label="Crea el usuario claudebot, descarga el agente de Automationia y programa el cron." code={buildRootCommand(info, user)} />

              <h6 className="mt-3">2. Claude Code para claudebot <span className="text-muted fw-normal small">(te pedirá entrar en claude.ai)</span></h6>
              <Block label="Una vez por servidor." code={CLAUDE_COMMAND} />

              <h6 className="mt-3">3. Configurar cada proyecto</h6>
              {!info.projects.length && (
                <div className="small text-muted mb-2">Este servidor aún no tiene proyectos conectados. Conéctalos en la pestaña <strong>Proyectos conectados</strong> y vuelve aquí: saldrá un bloque por proyecto.</div>
              )}
              {info.projects.map((p) => (
                <Block key={p.id} label={`#${p.id} · ${p.titulo}`} code={buildProjectCommand(p)} />
              ))}
              <div className="small text-muted mt-2">
                Esto solo crea el fichero de configuración. La guía completa de cada proyecto (copia de staging, usuario MySQL, reglas para Claude, publicación) está en Cambios → Proyectos conectados → <strong>Configurar en el servidor</strong>.
              </div>

              <h6 className="mt-3">4. Probar</h6>
              <Block label="Crea un ajuste de prueba, pulsa Enviar a Claude y lanza el agente a mano." code={`sudo -u claudebot /opt/ajustes/runner.sh\nls /opt/ajustes/runs/      # una carpeta por trabajo, con el ajuste y la respuesta de Claude`} />
            </>
          )}
        </div>
        <div className="card-footer d-flex justify-content-end">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
};

export default AgentInstallModal;
